import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ShieldCheck,
  ExternalLink,
  Loader2,
  RefreshCcw,
  FileText,
  AlertTriangle,
  X,
} from "lucide-react";
import {
  useBridgeStatus,
  useStartBridge,
  useSimulateBridgeApproval,
} from "../hooks/useBridge";
import { BluPayApiError } from "../api/client";
import { ErrorNotice } from "../components/errors/ErrorNotice";

const IS_DEV = process.env.NODE_ENV === "development";

function VerifiedCard({ title, body, onContinue }) {
  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left flex items-center justify-center px-5">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-6 text-center max-w-md w-full">
        <div className="w-16 h-16 rounded-full bg-[#E8F5E1] flex items-center justify-center mx-auto mb-5">
          <ShieldCheck size={28} className="text-[#0A4730]" />
        </div>

        <h2 className="text-lg font-semibold text-[#0A2540] mb-2">
          {title}
        </h2>

        <p className="text-sm text-[#0A2540]/50 leading-relaxed max-w-sm mx-auto mb-6">
          {body}
        </p>

        <button
          onClick={onContinue}
          className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98]"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function Header({ onBack }) {
  const navigate = useNavigate();
  const handleBack = onBack || (() => navigate(-1));

  return (
    <>
      {/* HEADER - mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>

          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">
              Account
            </p>

            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Identity Verification
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={handleBack}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>

        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Identity Verification
        </h1>
      </div>
    </>
  );
}

function BridgeKyc() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showTosOverlay, setShowTosOverlay] = useState(false);

  const { bridge, isLoadingStatus, refetchStatus } = useBridgeStatus();
  const { startBridge, isStarting } = useStartBridge();
  const { simulateApproval, isSimulating } = useSimulateBridgeApproval();

  // Route to /transfer ONLY if origin was /transfer, otherwise go to /home
  const destinationRoute =
    location.state?.from === "/transfer" || location.state?.fromTransfer
      ? "/transfer"
      : "/home";

  const isTosPending =
    bridge?.tos_link &&
    bridge?.tos_status !== "approved" &&
    bridge?.tos_status !== "completed";

  async function handleStart() {
    setError(null);

    try {
      await startBridge();
    } catch (err) {
      setError(
        err instanceof BluPayApiError
          ? err
          : "Couldn't start verification. Please try again.",
      );
    }
  }

  async function handleRefresh() {
    setError(null);

    if (isTosPending) {
      setShowTosOverlay(true);
      return;
    }

    setRefreshing(true);

    try {
      await refetchStatus();
    } catch (err) {
      setError(
        err instanceof BluPayApiError
          ? err
          : "Couldn't load your verification status.",
      );
    } finally {
      setRefreshing(false);
    }
  }

  function handleBackNavigation() {
    if (isTosPending) {
      setShowTosOverlay(true);
    } else {
      navigate(-1);
    }
  }

  async function handleSimulateApproval() {
    setError(null);

    try {
      await simulateApproval();
    } catch {
      setError(
        "Simulate approval failed. Check engineering logs for details.",
      );
    }
  }

  if (isLoadingStatus) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <p className="text-sm text-[#0A2540]/40">
          Loading…
        </p>
      </div>
    );
  }

  if (bridge?.ready) {
    return (
      <VerifiedCard
        title="You're verified"
        body="Your identity has been confirmed. You can continue setting up your payout details."
        onContinue={() => navigate(destinationRoute)}
      />
    );
  }

  const hasLinks = bridge?.kyc_link || bridge?.tos_link;

  return (
    <div className="min-h-screen bg-transparent pb-24 text-left relative">
      <Header onBack={handleBackNavigation} />

      {/* TOS OVERLAY MODAL */}
      {showTosOverlay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-5">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 relative animate-in fade-in zoom-in-95">
            <button
              onClick={() => setShowTosOverlay(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1"
            >
              <X size={20} />
            </button>

            <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} />
            </div>

            <div className="text-center space-y-2 mb-6">
              <h3 className="text-xl font-bold text-[#0A2540]">
                Terms of Service Required
              </h3>

              <p className="text-sm text-[#0A2540]/60 leading-relaxed">
                You must accept the Terms of Service to complete identity
                verification and proceed.
              </p>
            </div>

            {bridge?.tos_link && (
              <a
                href={bridge.tos_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowTosOverlay(false)}
                className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#0A4730]/90 transition shadow-lg shadow-[#0A4730]/20 mb-3"
              >
                Accept Terms of Service
                <ExternalLink size={18} />
              </a>
            )}

            <button
              onClick={() => {
                setShowTosOverlay(false);
                navigate(-1);
              }}
              className="w-full py-3 text-sm font-semibold text-slate-400 hover:text-slate-600 text-center"
            >
              I'll do this later
            </button>
          </div>
        </div>
      )}

      <div className="max-w-xl mx-auto px-5 py-8 space-y-6">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#E8F5E1] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck size={28} className="text-[#0A4730]" />
          </div>

          <h2 className="text-lg font-semibold text-[#0A2540] mb-2">
            Verify it's you
          </h2>

          <p className="text-sm text-[#0A2540]/50 leading-relaxed max-w-sm mx-auto">
            We need to do a quick identity check and Terms of Service
            confirmation before transfers can move.
          </p>
        </div>

        <ErrorNotice error={error} />

        {!hasLinks ? (
          <button
            onClick={handleStart}
            disabled={isStarting}
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isStarting && (
              <Loader2 size={16} className="animate-spin" />
            )}

            {isStarting
              ? "Starting…"
              : "Start Verification"}
          </button>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-5 space-y-3">
              {IS_DEV && bridge.customer_id && (
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-xs text-[#0A2540]/50 mb-1">
                    Bridge customer ID (dev only)
                  </p>

                  <p className="text-sm font-mono text-[#0A2540] break-all">
                    {bridge.customer_id}
                  </p>
                </div>
              )}

              {/* IDENTITY CHECK LINK */}
              {bridge.kyc_link && (
                <a
                  href={bridge.kyc_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-3 rounded-2xl border border-slate-100 hover:border-[#0A4730]/30 transition"
                >
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">
                      Complete identity check
                    </p>

                    <p className="text-xs text-[#0A2540]/40">
                      Status: {bridge.kyc_status || "pending"}
                    </p>
                  </div>

                  <ExternalLink
                    size={16}
                    className="text-[#0A4730]/60 shrink-0"
                  />
                </a>
              )}

              {/* HIGH-VISIBILITY TERMS OF SERVICE LINK */}
              {bridge.tos_link && (
                <a
                  href={bridge.tos_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-between px-4 py-3.5 rounded-2xl border transition ${
                    isTosPending
                      ? "border-amber-300 bg-amber-50/60 ring-2 ring-amber-400/20"
                      : "border-slate-100 hover:border-[#0A4730]/30"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText
                      size={20}
                      className={
                        isTosPending
                          ? "text-amber-600"
                          : "text-[#0A4730]/60"
                      }
                    />

                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[#0A2540]">
                          Accept Terms of Service
                        </p>

                        {isTosPending && (
                          <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                            Required
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-[#0A2540]/50">
                        Status: {bridge.tos_status || "pending"}
                      </p>
                    </div>
                  </div>

                  <ExternalLink
                    size={16}
                    className={
                      isTosPending
                        ? "text-amber-600"
                        : "text-[#0A4730]/60"
                    }
                  />
                </a>
              )}

              {IS_DEV && (
                <button
                  onClick={handleSimulateApproval}
                  disabled={isSimulating}
                  className="w-full h-10 rounded-xl bg-slate-200 text-[#0A2540] text-sm font-semibold hover:bg-slate-300 transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isSimulating && (
                    <Loader2
                      size={14}
                      className="animate-spin"
                    />
                  )}

                  {isSimulating
                    ? "Simulating…"
                    : "Simulate approval (dev only)"}
                </button>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full h-14 rounded-2xl bg-white border border-slate-200 text-[#0A4730] font-bold tracking-wide hover:border-[#0A4730]/30 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {refreshing ? (
                <Loader2
                  size={16}
                  className="animate-spin"
                />
              ) : (
                <RefreshCcw size={16} />
              )}

              {refreshing
                ? "Checking…"
                : "I've completed this — check status"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Kyc() {
  return <BridgeKyc />;
}